import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  UserIcon,
  CalendarIcon,
  TrendingUpIcon,
  ClockIcon,
  PlusIcon,
  ArrowRightIcon,
  UsersIcon,
  CheckCircle2Icon,
  ActivityIcon,
  TargetIcon,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Funnel,
  FunnelChart,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'

interface Lead {
  id: number
  name: string
  phone: string | null
  vehicle_type: string
  urgency: string
  status: string
  created_at: string
}

interface Appointment {
  id: number
  lead_name: string
  time: string
  appointment_type: string
  status: string
}

interface LeadsStats {
  total: number
  active: number
  new: number
  contacted: number
  estimate_requested: number
  appointment_scheduled: number
  won: number
  lost: number
  converted: number
}

interface FunnelStage {
  stage: string
  value: number
  percentage: number
}

interface SourceBreakdown {
  source: string
  count: number
  percentage: number
}

interface StatusDistribution {
  status: string
  count: number
  color: string
}

interface ConversionMetrics {
  lead_to_contact_rate: number
  contact_to_estimate_rate: number
  estimate_to_appointment_rate: number
  appointment_to_won_rate: number
  overall_conversion_rate: number
  win_rate: number
  loss_rate: number
}

interface TrendData {
  date: string
  leads: number
  contacted: number
  won: number
}

interface AppointmentVolume {
  today: number
  this_week: number
  this_month: number
  upcoming: number
}

interface AppointmentLead {
  id: number
  name: string
}

interface DashboardProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  leads_stats: LeadsStats
  funnel_data: FunnelStage[]
  source_breakdown: SourceBreakdown[]
  status_distribution: StatusDistribution[]
  conversion_metrics: ConversionMetrics
  trends_data: TrendData[]
  appointment_volume: AppointmentVolume
  recent_leads: Lead[]
  today_appointments: Appointment[]
  leads: AppointmentLead[]
  current_period: string
}

const trendsChartConfig = {
  leads: {
    label: 'New Leads',
    color: '#5589ed',
  },
  contacted: {
    label: 'Contacted',
    color: '#a855f7',
  },
  won: {
    label: 'Won',
    color: '#22c55e',
  },
} satisfies ChartConfig

const FUNNEL_COLORS = ['#5589ed', '#a855f7', '#6366f1', '#f97316', '#22c55e']

export default function Dashboard({
  auth,
  leads_stats,
  funnel_data,
  source_breakdown,
  status_distribution,
  conversion_metrics,
  trends_data,
  appointment_volume,
  recent_leads,
  today_appointments,
  leads,
  current_period,
}: DashboardProps) {
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'Emergency':
        return 'bg-red-600 hover:bg-red-700'
      case 'High':
        return 'bg-orange-600 hover:bg-orange-700'
      case 'Medium':
        return 'bg-yellow-600 hover:bg-yellow-700'
      case 'Low':
        return 'bg-green-600 hover:bg-green-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  const handlePeriodChange = (period: string) => {
    router.get('/dashboard', { period }, { preserveScroll: true })
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 lg:px-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Shop Lead Dashboard</h1>
              <p className="text-muted-foreground">Here's what's happening with your leads today</p>
            </div>
            <Select value={current_period} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stats Grid */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card className="rounded-2xl border-2 border-[#5589ed]/20 bg-white hover:border-[#5589ed] hover:shadow-lg transition-all">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[#151515]">Total Leads</CardTitle>
                  <div className="inline-flex size-10 items-center justify-center rounded-xl bg-[#5589ed]/10">
                    <TrendingUpIcon className="size-5 text-[#5589ed]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#151515]">{leads_stats.total}</div>
                  <p className="text-sm text-[#666666]">All leads in pipeline</p>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-2 border-[#5589ed]/20 bg-white hover:border-[#5589ed] hover:shadow-lg transition-all">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[#151515]">Active Leads</CardTitle>
                  <div className="inline-flex size-10 items-center justify-center rounded-xl bg-[#5589ed]/10">
                    <ActivityIcon className="size-5 text-[#5589ed]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#151515]">{leads_stats.active}</div>
                  <p className="text-sm text-[#666666]">In progress</p>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-2 border-[#5589ed]/20 bg-white hover:border-[#5589ed] hover:shadow-lg transition-all">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[#151515]">Converted</CardTitle>
                  <div className="inline-flex size-10 items-center justify-center rounded-xl bg-[#5589ed]/10">
                    <CheckCircle2Icon className="size-5 text-[#5589ed]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#151515]">{leads_stats.converted}</div>
                  <p className="text-sm text-[#666666]">Jobs completed</p>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-2 border-[#5589ed]/20 bg-white hover:border-[#5589ed] hover:shadow-lg transition-all">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[#151515]">Conversion Rate</CardTitle>
                  <div className="inline-flex size-10 items-center justify-center rounded-xl bg-[#5589ed]/10">
                    <TargetIcon className="size-5 text-[#5589ed]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#151515]">
                    {conversion_metrics.overall_conversion_rate}%
                  </div>
                  <p className="text-sm text-[#666666]">Overall success rate</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Funnel and Trends */}
          <div className="grid gap-4 px-4 lg:px-6 lg:grid-cols-2 min-w-0">
            {/* Sales Funnel */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Sales Funnel</CardTitle>
                <p className="text-sm text-muted-foreground">Lead progression through pipeline</p>
              </CardHeader>
              <CardContent className="min-w-0 space-y-4">
                <ResponsiveContainer width="100%" height={300}>
                  <FunnelChart>
                    <Funnel dataKey="value" data={funnel_data} isAnimationActive>
                      {funnel_data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={FUNNEL_COLORS[index % FUNNEL_COLORS.length]} />
                      ))}
                      <Label
                        position="center"
                        content={({ viewBox, value }) => {
                          if (viewBox && 'x' in viewBox && 'y' in viewBox) {
                            const stage = funnel_data.find((d) => d.value === value)
                            return (
                              <text
                                x={viewBox.x}
                                y={viewBox.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="fill-background"
                              >
                                <tspan x={viewBox.x} y={(viewBox.y || 0) - 8} className="text-sm font-semibold">
                                  {stage?.stage}
                                </tspan>
                                <tspan x={viewBox.x} y={(viewBox.y || 0) + 12} className="text-lg font-bold">
                                  {value} ({stage?.percentage}%)
                                </tspan>
                              </text>
                            )
                          }
                        }}
                      />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
                {/* Funnel Legend */}
                <div className="flex flex-wrap justify-center gap-3 pt-2">
                  {funnel_data.map((stage, index) => (
                    <div key={stage.stage} className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-sm"
                        style={{ backgroundColor: FUNNEL_COLORS[index % FUNNEL_COLORS.length] }}
                      />
                      <span className="text-xs text-muted-foreground">{stage.stage}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Trends Chart */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Lead Trends</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {current_period === 'daily'
                    ? 'Last 7 days'
                    : current_period === 'monthly'
                      ? 'Last 6 months'
                      : 'Last 8 weeks'}
                </p>
              </CardHeader>
              <CardContent className="min-w-0 space-y-4">
                <ChartContainer config={trendsChartConfig} className="h-[300px] w-full">
                  <AreaChart data={trends_data} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(value) => value}
                    />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="leads"
                      stackId="1"
                      stroke="var(--color-leads)"
                      fill="var(--color-leads)"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="contacted"
                      stackId="2"
                      stroke="var(--color-contacted)"
                      fill="var(--color-contacted)"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="won"
                      stackId="3"
                      stroke="var(--color-won)"
                      fill="var(--color-won)"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ChartContainer>
                {/* Trends Legend */}
                <div className="flex flex-wrap justify-center gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#5589ed' }} />
                    <span className="text-xs text-muted-foreground">New Leads</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#a855f7' }} />
                    <span className="text-xs text-muted-foreground">Contacted</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
                    <span className="text-xs text-muted-foreground">Won</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Source Breakdown and Status Distribution */}
          <div className="grid gap-4 px-4 lg:px-6 lg:grid-cols-2 min-w-0">
            {/* Lead Source Breakdown */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Lead Sources</CardTitle>
                <p className="text-sm text-muted-foreground">Where your leads come from</p>
              </CardHeader>
              <CardContent className="min-w-0">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={source_breakdown}
                      dataKey="count"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ source, percentage }) => `${source} (${percentage}%)`}
                    >
                      {source_breakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={FUNNEL_COLORS[index % FUNNEL_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload
                          return (
                            <div className="rounded-lg border bg-background p-2 shadow-md">
                              <div className="font-medium">{data.source}</div>
                              <div className="text-sm text-muted-foreground">
                                {data.count} leads ({data.percentage}%)
                              </div>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
                <p className="text-sm text-muted-foreground">Current lead statuses</p>
              </CardHeader>
              <CardContent className="min-w-0">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={status_distribution} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <XAxis
                      dataKey="status"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(value) => value.substring(0, 8)}
                    />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload
                          return (
                            <div className="rounded-lg border bg-background p-2 shadow-md">
                              <div className="font-medium">{data.status}</div>
                              <div className="text-sm text-muted-foreground">{data.count} leads</div>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {status_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Conversion Metrics and Appointment Volume */}
          <div className="grid gap-4 px-4 lg:px-6 lg:grid-cols-2 min-w-0">
            {/* Conversion Metrics */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Conversion Metrics</CardTitle>
                <p className="text-sm text-muted-foreground">Pipeline efficiency rates</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Lead → Contact</span>
                    <Badge variant="outline" className="rounded-full">
                      {conversion_metrics.lead_to_contact_rate}%
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Contact → Estimate</span>
                    <Badge variant="outline" className="rounded-full">
                      {conversion_metrics.contact_to_estimate_rate}%
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Estimate → Appointment</span>
                    <Badge variant="outline" className="rounded-full">
                      {conversion_metrics.estimate_to_appointment_rate}%
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Appointment → Won</span>
                    <Badge variant="outline" className="rounded-full">
                      {conversion_metrics.appointment_to_won_rate}%
                    </Badge>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">Win Rate</span>
                      <Badge className="bg-green-600 hover:bg-green-700 rounded-full">
                        {conversion_metrics.win_rate}%
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Loss Rate</span>
                    <Badge className="bg-gray-600 hover:bg-gray-700 rounded-full">
                      {conversion_metrics.loss_rate}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Appointment Volume */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader>
                <CardTitle>Appointment Volume</CardTitle>
                <p className="text-sm text-muted-foreground">Scheduled repairs and diagnostics</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-xl border-2 border-[#5589ed]/20 bg-[#5589ed]/5">
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="size-5 text-[#5589ed]" />
                      <span className="font-medium">Today</span>
                    </div>
                    <span className="text-2xl font-bold text-[#5589ed]">{appointment_volume.today}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl border-2 border-gray-200">
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="size-5 text-muted-foreground" />
                      <span className="font-medium">This Week</span>
                    </div>
                    <span className="text-2xl font-bold">{appointment_volume.this_week}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl border-2 border-gray-200">
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="size-5 text-muted-foreground" />
                      <span className="font-medium">This Month</span>
                    </div>
                    <span className="text-2xl font-bold">{appointment_volume.this_month}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl border-2 border-orange-600/20 bg-orange-500/5">
                    <div className="flex items-center gap-3">
                      <ClockIcon className="size-5 text-orange-600" />
                      <span className="font-medium">Upcoming</span>
                    </div>
                    <span className="text-2xl font-bold text-orange-600">{appointment_volume.upcoming}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Leads and Today's Appointments */}
          <div className="grid gap-4 px-4 lg:px-6 lg:grid-cols-2 min-w-0">
            {/* Recent Leads */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent Leads</CardTitle>
                  <p className="text-sm text-muted-foreground">Latest lead activity</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => router.visit('/leads')}
                  data-testid="view-all-leads-button"
                  className="text-[#5589ed] hover:text-[#4478dc]"
                >
                  View All
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {recent_leads.length > 0 ? (
                  <div className="space-y-3">
                    {recent_leads.map((lead) => (
                      <div
                        key={lead.id}
                        className="flex items-start justify-between p-3 rounded-xl border-2 border-gray-200 cursor-pointer transition-all hover:border-[#5589ed] hover:shadow-lg"
                        onClick={() => router.visit(`/leads/${lead.id}`)}
                      >
                        <div className="space-y-1">
                          <div className="font-medium">{lead.name}</div>
                          <div className="text-sm text-muted-foreground">{lead.vehicle_type}</div>
                          <div className="flex gap-2">
                            <Badge className={`${getUrgencyColor(lead.urgency)} text-xs rounded-full`}>
                              {lead.urgency}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No recent leads</div>
                )}
              </CardContent>
            </Card>

            {/* Today's Appointments */}
            <Card className="rounded-2xl min-w-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Today's Appointments</CardTitle>
                  <p className="text-sm text-muted-foreground">Scheduled for today</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => router.visit('/appointments')}
                  data-testid="view-calendar-button"
                  className="text-[#5589ed] hover:text-[#4478dc]"
                >
                  View Calendar
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {today_appointments.length > 0 ? (
                  <div className="space-y-3">
                    {today_appointments.map((apt) => (
                      <div
                        key={apt.id}
                        className="flex items-start justify-between p-3 rounded-xl border-2 border-gray-200 cursor-pointer transition-all hover:border-[#5589ed] hover:shadow-lg"
                        onClick={() => router.visit(`/appointments/${apt.id}`)}
                      >
                        <div className="space-y-1">
                          <div className="font-medium">{apt.lead_name}</div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <ClockIcon className="size-4" />
                            <span>{apt.time}</span>
                          </div>
                          <Badge variant="outline" className="text-xs rounded-full">
                            {apt.appointment_type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No appointments scheduled for today
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
